import {
  SecurityMapViewModel,
  SecurityMappingMapType,
  SecuritySiteCountryModel,
} from "src/app/store/models/security-mapping.model";
import { Capacitor } from "@capacitor/core";
import { environment } from "src/environments/environment";
import { UserModel } from "src/app/store/models/user.model";
import { Loader, Library } from "@googlemaps/js-api-loader";
import { MAPBACKGROUND } from "src/app/utils/constant.util";
import { SiteModel } from "src/app/store/models/asset.model";
import { inject, ElementRef, Injectable } from "@angular/core";
import { SecurityMappingCountryService } from "./security-mapping-country.service";

const SITE_ZOOM = 14;
const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 20, lng: 0 };
// Keeps the country outline clear of the toolbar (search / Add) and the map edges
const COUNTRY_FIT_PADDING: google.maps.Padding = {
  top: 72,
  left: 24,
  right: 24,
  bottom: 24,
};
// Point and risk area names always beside their pins, coloured for the map type
// (styles: security-map-markers mixin)
const NAMES_VISIBLE_CLASS = "security-map-names-visible";
const SATELLITE_MAP_CLASS = "security-map-on-satellite";
const SATELLITE_MAP_TYPES = ["satellite", "hybrid"];

@Injectable({ providedIn: "root" })
export class SecurityMappingMapService {
  private securityMappingCountry = inject(SecurityMappingCountryService);

  public map: google.maps.Map | null = null;
  private countryOutlines: google.maps.Polygon[] = [];
  // Points / risk areas can only be added inside the site's country (developer's rule)
  private country: SecuritySiteCountryModel | null = null;
  private cursor: string | null = null;
  // The cursor applies only inside the country; outside, the normal hand is shown
  private isCursorCountryOnly = false;
  private cursorFrame: number | null = null;
  private cursorListener: google.maps.MapsEventListener | null = null;
  private projectionOverlay: google.maps.OverlayView | null = null;

  // ** Google Maps Loader ** //
  private readonly loader: Loader = new Loader({
    region: "US",
    language: "en",
    version: "3.64",
    apiKey: this.mapKey(),
  });
  // ** Google Maps Loader ** //

  // ** Initialize Map
  public async InitializeMap(
    mapRef: ElementRef<HTMLDivElement>,
    site: SiteModel | null,
    user: UserModel | null,
  ): Promise<void> {
    const { Map, StyledMapType } = await this.importMapsLibrary("maps");

    this.map = new Map(mapRef.nativeElement, {
      tilt: 0,
      minZoom: 2,
      maxZoom: 21,
      mapId: "g-maps",
      zoom: SITE_ZOOM,
      draggable: true,
      mapTypeControl: false,
      disableDefaultUI: true,
      gestureHandling: "greedy",
      streetViewControl: false,
      keyboardShortcuts: false,
      disableDoubleClickZoom: true,
      center: this.getSiteCenter(site),
    });

    const styledMap = new StyledMapType(MAPBACKGROUND, { name: "Base" });
    this.map.mapTypes.set("styled_map", styledMap);
    this.map.setMapTypeId(this.getPreferredMapType(user));
    this.watchMapType(this.map, mapRef.nativeElement);

    // Empty overlay used for its projection (lat/lng -> container pixel), to tell
    // whether a position is beside the forms' side panel or under it
    const ProjectionOverlay = class extends google.maps.OverlayView {
      override onAdd(): void {}
      override draw(): void {}
      override onRemove(): void {}
    };
    this.projectionOverlay = new ProjectionOverlay();
    this.projectionOverlay.setMap(this.map);
  }
  // ** Initialize Map

  // ** Map Controls
  public ZoomIn(): void {
    if (!this.map) return;
    this.map.setZoom((this.map.getZoom() ?? SITE_ZOOM) + 1);
  }

  public ZoomOut(): void {
    if (!this.map) return;
    this.map.setZoom((this.map.getZoom() ?? SITE_ZOOM) - 1);
  }

  public CenterOnSite(site: SiteModel | null): void {
    if (!this.map) return;
    this.map.setZoom(SITE_ZOOM);
    this.map.panTo(this.getSiteCenter(site));
  }

  public SetMapType(mapType: SecurityMappingMapType): void {
    this.map?.setMapTypeId(mapType);
  }
  // ** Map Controls

  // isCountryOnly: the cursor (e.g. the crosshair for adding a point) is shown only over
  // the site's country; outside it the map keeps its normal hand, as clicks there do nothing
  public SetCursor(cursor: string | null, isCountryOnly = false): void {
    this.cursor = cursor;
    this.isCursorCountryOnly = isCountryOnly;
    this.map?.setOptions({ draggableCursor: cursor });
  }

  // True without a known country (nothing to limit to)
  public IsInsideCountry(position: google.maps.LatLngLiteral): boolean {
    return (
      !this.country ||
      this.securityMappingCountry.IsInside(this.country, position)
    );
  }

  // Zoom in (never out) and centre on a position, e.g. so a small risk area is visible.
  // padding: map edges covered by panels (the forms' side panel): centred in the rest.
  public FocusOn(
    position: google.maps.LatLngLiteral,
    minZoom: number,
    padding: google.maps.Padding = {},
  ): void {
    if (!this.map) return;
    if ((this.map.getZoom() ?? 0) < minZoom) this.map.setZoom(minZoom);
    this.panToVisibleCenter(position, padding);
  }

  // Pans only when the position is off screen or under a panel (padding)
  public PanIntoView(
    position: google.maps.LatLngLiteral,
    padding: google.maps.Padding = {},
  ): void {
    if (!this.map) return;
    const { top = 0, left = 0, right = 0, bottom = 0 } = padding;
    const mapElement = this.map.getDiv();
    const pixel = this.projectionOverlay
      ?.getProjection()
      ?.fromLatLngToContainerPixel(position);
    const isVisible =
      !!pixel &&
      pixel.x >= left &&
      pixel.y >= top &&
      pixel.x <= mapElement.clientWidth - right &&
      pixel.y <= mapElement.clientHeight - bottom;
    if (!isVisible) this.panToVisibleCenter(position, padding);
  }

  // A click on a Google place icon is an IconMouseEvent with its placeId
  public OnMapEvent(
    eventName: "click" | "idle" | "mousemove" | "mouseout",
    handler: (
      event: google.maps.MapMouseEvent | google.maps.IconMouseEvent,
    ) => void,
  ): google.maps.MapsEventListener | null {
    return this.map ? this.map.addListener(eventName, handler) : null;
  }

  public async ImportMarkerLibrary(): Promise<google.maps.MarkerLibrary> {
    return (await this.loader.importLibrary(
      "marker",
    )) as google.maps.MarkerLibrary;
  }

  public async ImportGeometryLibrary(): Promise<google.maps.GeometryLibrary> {
    return (await this.loader.importLibrary(
      "geometry",
    )) as google.maps.GeometryLibrary;
  }

  public async ImportRoutesLibrary(): Promise<google.maps.RoutesLibrary> {
    return (await this.loader.importLibrary(
      "routes",
    )) as google.maps.RoutesLibrary;
  }

  public async ImportPlacesLibrary(): Promise<google.maps.PlacesLibrary> {
    return (await this.loader.importLibrary(
      "places",
    )) as google.maps.PlacesLibrary;
  }

  public async ImportGeocodingLibrary(): Promise<google.maps.GeocodingLibrary> {
    return (await this.loader.importLibrary(
      "geocoding",
    )) as google.maps.GeocodingLibrary;
  }

  // Shows the whole path, leaving room for the panels over the map
  public FitPath(
    path: google.maps.LatLngLiteral[],
    padding: google.maps.Padding,
  ): void {
    if (!this.map || path.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    path.forEach((position) => bounds.extend(position));
    this.map.fitBounds(bounds, padding);
  }

  // Site's country outlined and fitted: the zoomed-out start view of both steps
  // (Figma 26839:1041, developer's request). No name on the map: only icons, names on
  // hover (developer's request 2026-10-05)
  public ShowCountry(country: SecuritySiteCountryModel | null): void {
    const map = this.map;
    if (!map || !country) return;
    this.removeCountry();
    this.country = country;
    this.listenForCountryCursor(map);
    this.countryOutlines = this.securityMappingCountry.DrawOutline(
      map,
      country,
    );
    map.fitBounds(
      this.securityMappingCountry.GetBounds(country),
      COUNTRY_FIT_PADDING,
    );
  }

  public DestroyMap(): void {
    this.removeCountry();
    this.projectionOverlay?.setMap(null);
    this.projectionOverlay = null;
    this.map = null;
  }

  // Centre of the map moved so the position sits in the middle of the part not covered
  // by panels (world coordinates: 1 px at zoom z = 1 / 2^z world units)
  private panToVisibleCenter(
    position: google.maps.LatLngLiteral,
    padding: google.maps.Padding,
  ): void {
    const map = this.map;
    const projection = map?.getProjection();
    if (!map || !projection) {
      map?.panTo(position);
      return;
    }
    const { top = 0, left = 0, right = 0, bottom = 0 } = padding;
    const scale = 2 ** (map.getZoom() ?? SITE_ZOOM);
    const point = projection.fromLatLngToPoint(position);
    const center =
      point &&
      projection.fromPointToLatLng(
        new google.maps.Point(
          point.x + (right - left) / 2 / scale,
          point.y + (bottom - top) / 2 / scale,
        ),
      );
    map.panTo(center ?? position);
  }

  public getPreferredMapType(user: UserModel | null): SecurityMappingMapType {
    return user?.preferences?.map?.mapType === "styled_map"
      ? "styled_map"
      : "satellite";
  }

  // Names are always shown (developer's request 2026-10-14): white on satellite, navy
  // on the basic map, following the map type when the user switches it
  private watchMapType(map: google.maps.Map, mapElement: HTMLElement): void {
    mapElement.classList.add(NAMES_VISIBLE_CLASS);
    const applyMapType = (): void => {
      mapElement.classList.toggle(
        SATELLITE_MAP_CLASS,
        SATELLITE_MAP_TYPES.includes(map.getMapTypeId() ?? ""),
      );
    };
    applyMapType();
    map.addListener("maptypeid_changed", applyMapType);
  }

  private removeCountry(): void {
    this.countryOutlines.forEach((outline) => outline.setMap(null));
    this.countryOutlines = [];
    this.country = null;
    this.cursorListener?.remove();
    this.cursorListener = null;
    if (this.cursorFrame !== null) cancelAnimationFrame(this.cursorFrame);
    this.cursorFrame = null;
  }

  // Mouse only (touch has no hover): once per frame, crosshair inside the country,
  // the normal hand outside
  private listenForCountryCursor(map: google.maps.Map): void {
    let lastPosition: google.maps.LatLngLiteral | null = null;
    this.cursorListener = map.addListener(
      "mousemove",
      (event: google.maps.MapMouseEvent) => {
        if (!this.isCursorCountryOnly || !event.latLng) return;
        lastPosition = event.latLng.toJSON();
        if (this.cursorFrame !== null) return;
        this.cursorFrame = requestAnimationFrame(() => {
          this.cursorFrame = null;
          if (!this.isCursorCountryOnly || !lastPosition) return;
          const isInside = this.IsInsideCountry(lastPosition);
          map.setOptions({ draggableCursor: isInside ? this.cursor : null });
        });
      },
    );
  }

  private getSiteCenter(site: SiteModel | null): google.maps.LatLngLiteral {
    const coordinate = site?.locations?.site?.coordinate;
    return coordinate?.lat != null && coordinate?.lng != null
      ? { lat: coordinate.lat, lng: coordinate.lng }
      : DEFAULT_CENTER;
  }

  // ** Returns correct API key based on platform
  private mapKey(): string {
    return Capacitor.isNativePlatform()
      ? environment.nativeMapKey
      : environment.browserMapKey;
  }

  private async importMapsLibrary(
    type: Library,
  ): Promise<google.maps.MapsLibrary> {
    return (await this.loader.importLibrary(type)) as google.maps.MapsLibrary;
  }

  // The camera now, to come back to it later (e.g. after zooming to a directions step)
  public GetView(): SecurityMapViewModel | null {
    const center = this.map?.getCenter();
    const zoom = this.map?.getZoom();
    if (!center || zoom === undefined) return null;
    return { center: center.toJSON(), zoom };
  }

  public SetView(view: SecurityMapViewModel): void {
    this.map?.moveCamera({ center: view.center, zoom: view.zoom });
  }
}
